import { useEffect, useState } from "react";
import { useRequestFinished } from "../../../common/hooks";
import { connect } from "react-redux";
import { useIntl } from "react-intl";
import TooltipButton from "../../../common/tooltipButton";
import { useNavigate, useParams } from "react-router-dom";
import { Editor } from "../../../common/fields";
import CustomBreadCrumbs from "../../../common/breadcrumbs";
import Form from "../../../common/form";
import {
  resetUpload,
  uploadRequest,
} from "../../../../store/slices/uploadSlice";
import {
  createRequest,
  detailsRequest,
  updateDetailRequest,
} from "../../../../store/slices/learningObjectsSlice";
import LearningObjectDetailsForm from "./LearningObjectDetailsForm";
import Section from "../../../common/section";
import {
  AccessScopeConstants,
  GlobalConstants,
  LearningObjectConstants,
} from "../../../../constants";
import FileUpload from "../../../common/fileUpload";
import * as RoleHelper from "../../../../helpers/RoleHelper";
import { listRequest as certificateListRequest } from "../../../../store/slices/certificateSlice";
import { listRequest as rewardListRequest } from "../../../../store/slices/rewardSlice";
import { useMountEffect } from "primereact/hooks";
import {
  PICKER_PAGE_SIZE,
  useServerSearch,
} from "../../../common/hooks/useServerSearch";

const LearningObjectDetails = ({
  action,
  certificateListRequest,
  certificates,
  createRequest,
  detailsRequest,
  enterprise,
  error,
  labels,
  menu,
  progress,
  record,
  requesting,
  resetUpload,
  rewardListRequest,
  rewards,
  updateDetailRequest,
  uploadDetails,
  uploadErrors,
  uploading,
  uploadRequest,
  user,
}) => {
  const intl = useIntl();

  const params = useParams();
  const [fileName, setFileName] = useState("");
  const [bannerFileName, setBannerFileName] = useState("");
  const navigate = useNavigate();

  const [uploadType, setUploadType] = useState(null);
  const [isEditable, setIsEditable] = useState(params?.id ? false : true);
  const [htmlContent, setHtmlContent] = useState(record?.content || "");

  const { role } = user || {};
  const canUpdate = RoleHelper.checkRoleScope(
    role?.scope,
    AccessScopeConstants.ContentAccess,
    AccessScopeConstants.UpdateScope
  );

  // The reward picker: the first page by name, then the server's matches for
  // what the admin types (E2E25)
  const loadRewards = (namePattern = "") =>
    rewardListRequest({
      IsActive: true,
      pageSize: PICKER_PAGE_SIZE,
      pageNo: 1,
      sortBy: "asc",
      sortColumn: "name",
      namePattern,
    });
  const searchRewards = useServerSearch(loadRewards);

  useMountEffect(() => {
    resetUpload();
    if (params?.id) {
      detailsRequest({ id: params.id });
    }
    certificateListRequest();
    loadRewards();
  });

  // Only a save answered while this screen is open: the persisted store can
  // still say "update" from an earlier save when the screen mounts (F-BUG-12).
  const requestFinished = useRequestFinished(requesting);
  useEffect(() => {
    if ((action === "create" || action === "update") && requestFinished && !error) {
      navigate(menu.to);
    }
  }, [error, action, requestFinished, menu, navigate]);

  const selectedType = LearningObjectConstants.TYPES.find(
    (type) =>
      type.value === uploadType || record?.type?.toLowerCase() === type.name
  );

  useEffect(() => {
    const HTMLType = LearningObjectConstants.TYPES.find(
      (item) => item.id === 4
    );

    if (record?.type.toLowerCase() === HTMLType.name.toLocaleLowerCase()) {
      setHtmlContent(record?.content);
    }
  }, [record]);

  const fileType = selectedType?.contentType || "*/*";

  const onBannerUpload = (event) => {
    const file = event.files[0];
    setBannerFileName(file.name);
    uploadRequest({ file, type: "image" });
  };

  const onUpload = (event) => {
    const file = event.files[0];
    setFileName(file.name);
    const type = uploadType;
    uploadRequest({ file, type });
  };

  const onSubmit = (values) => {
    const staticContentType =
      uploadType === 4 ? "text/html" : uploadType === 11 ? "link" : "";

    const payload = {
      ...values,
      bannerImageId:
        uploadDetails?.[bannerFileName]?.artifactId ||
        record?.bannerImageId ||
        null,
      isCertified: false,
      supportedLanguages: values.supportedLanguages.map((lang) => lang.code),
      certificateId:
        values?.certificateId === "no-cert"
          ? null
          : values.certificateId || null,
      maxAttemptsAllowed: 0,
      pointsOnCompletion: values.pointsOnCompletion || 0,
      maxTimeInSecsToComplete: values?.maxTimeInSecsAllowed * 60,
      maxTimeInSecsAllowed: values?.maxTimeInSecsAllowed * 60,
      masteryScore:
        parseInt(uploadDetails?.[fileName]?.masteryScore) ||
        record?.masteryScore,
      artifactId: uploadDetails?.[fileName]?.artifactId || record?.artifactId,
      manifestFilePath:
        uploadDetails?.[fileName]?.manifestFilePath || record?.manifestFilePath,
      contentType:
        uploadDetails?.[fileName]?.contentType ||
        uploadDetails?.[fileName]?.courseType ||
        staticContentType ||
        record?.contentType,
      content:
        uploadType === 4
          ? htmlContent
          : uploadType === 11
          ? values.content
          : null,
      // Convert 'no_reward' back to 0 for API
      rewardTriggerType:
        values.rewardTriggerType === "no_reward" ? 0 : values.rewardTriggerType,
    };
    if (!params.id) {
      createRequest(payload);
    } else {
      updateDetailRequest({ id: params.id, payload });
    }
  };

  const uploadableTypes = LearningObjectConstants.TYPES.filter(
    (item) => item.contentType
  ).map((item) => item.id);

  return (
    <div className="pb-8 md:pb-0">
      <CustomBreadCrumbs
        labels={labels}
        navigate={navigate}
        editLabel={record?.name}
      />
      <div className="edexlms-page-content">
        <div className="edexlms-page-header">
          <div className="edexlms-page-title">
            {intl.formatMessage({
              id: params?.id ? "edit_learning_object" : "add_learning_object",
            })}
          </div>
          {(params?.id && !isEditable && (
            <div className="edexlms-page-actions">
              <TooltipButton
                icon="pi pi-pencil"
                outlined
                onClick={() =>
                  (record?.id && setIsEditable(!isEditable)) || null
                }
                label={intl.formatMessage({ id: "edit" })}
                type="button"
                size="small"
                disabled={!canUpdate || !record?.id}
                tooltipOptions={{ showOnDisabled: true, position: "bottom" }}
                tooltip={
                  canUpdate && !record?.isSystemDefined
                    ? ""
                    : intl.formatMessage({ id: "no_permission" })
                }
              />
            </div>
          )) ||
            null}
        </div>
        <div
          className="edexlms-page-body"
          style={{
            height: "calc(100vh - 64px - 20px - 45px - 40px - 14px - 33px)",
            overflowY: "scroll",
          }}
        >
          <Form
            initialValues={{
              certificateId: record?.certificateId || "no-cert",
              name: record?.name,
              type: selectedType?.id || null,
              maxAttemptsAllowed: record?.maxAttemptsAllowed,
              maxTimeInSecsAllowed:
                ((record?.maxTimeInSecsAllowed || 0) / 60).toFixed(0) || 0,
              pointsOnCompletion: record?.pointsOnCompletion || 0,
              rewardTriggerType:
                record?.rewardTriggerType !== undefined
                  ? !record?.rewardTriggerType
                    ? "no_reward"
                    : record?.rewardTriggerType
                  : "no_reward",
              rewardId: record?.rewardId,
              tags: record?.tags,
              content: record?.content || "",
              supportedLanguages: GlobalConstants.LANGUAGES.filter((lang) =>
                (record?.supportedLanguages || ["en"]).includes(lang.code)
              ).map((language) => ({
                ...language,
                id: language.code,
                name: language.name,
              })),
            }}
            disabled={
              (!params.id &&
                ((uploadableTypes.includes(uploadType) &&
                  !uploadDetails?.[fileName]) ||
                  (uploadType === 4 && !htmlContent))) ||
              !isEditable
            }
            submitBtn={{
              label: intl.formatMessage({
                id: params?.id
                  ? "update_learning_object"
                  : "add_learning_object",
              }),
              onClick: onSubmit,
            }}
            cancelBtn={{
              label: intl.formatMessage({ id: "cancel" }),
              onClick: () => navigate(menu?.to),
            }}
          >
            <div className="flex flex-column gap-4">
              <LearningObjectDetailsForm
                setUploadType={setUploadType}
                onRewardSearch={searchRewards}
                selectedReward={
                  (record?.rewardId && {
                    id: record.rewardId,
                    name: record.rewardName,
                  }) ||
                  null
                }
                uploadDetails={progress?.[fileName]}
                disabled={!isEditable}
                readOnly={params?.id ? true : false}
                certificates={[
                  {
                    name: intl.formatMessage({ id: "no_certificate" }),
                    id: "no-cert",
                  },
                  ...certificates,
                ]}
                rewards={Array.isArray(rewards) ? rewards : []}
                allowedLanguages={
                  enterprise?.languageConfiguration?.allowedLanguages
                }
              />
              <Section
                title="content_details"
                titleValues={{
                  type:
                    (selectedType?.name &&
                      intl.formatMessage({
                        id: selectedType?.name,
                      })) ||
                    "",
                }}
              >
                <div className="grid">
                  <div className="col-12">
                    <FileUpload
                      name="banner_file"
                      label="banner_image"
                      infoMessages={[
                        intl.formatMessage({
                          id: `upload_type_info_image`,
                        }),
                        intl.formatMessage(
                          {
                            id: `upload_file_dimensions`,
                          },
                          {
                            width: 1350,
                            height: 450,
                          }
                        ),
                      ]}
                      fileType={GlobalConstants.IMAGE_UPLOAD_FILE_TYPES}
                      isImage
                      fileUrl={
                        (record?.bannerImage &&
                          `${new URL(
                            record?.bannerImage,
                            process.env.REACT_APP_URL
                          ).toString()}`) ||
                        null
                      }
                      uploading={uploading?.[bannerFileName]}
                      progress={progress?.[bannerFileName] || 0}
                      onUpload={onBannerUpload}
                      readOnly={params?.id ? !isEditable : false}
                      uploadDetails={uploadDetails?.[bannerFileName]}
                      onClear={() => setBannerFileName("")}
                      maxFileSize={524288000 / 10}
                    />
                  </div>
                  {(uploadType &&
                    uploadableTypes.includes(uploadType) &&
                    ((params?.id && isEditable) || !params?.id) && (
                      <div className="col-12">
                        <FileUpload
                          infoMessages={[
                            intl.formatMessage({
                              id: `upload_type_info_${selectedType?.name}`,
                            }),
                            intl.formatMessage(
                              {
                                id: `upload_file_size`,
                              },
                              { size: selectedType?.maxSize || "1.5 GB" }
                            ),
                          ]}
                          required={true}
                          name="file"
                          label="learning_object_content"
                          fileType={fileType}
                          uploading={uploading?.[fileName]}
                          progress={progress?.[fileName] || 0}
                          onUpload={onUpload}
                          readOnly={params?.id ? !isEditable : false}
                          error={uploadErrors?.[fileName]}
                          messageAfterUpload={
                            !uploadDetails?.[fileName]?.artifactId
                              ? "verify_scorm"
                              : ""
                          }
                          maxFileSize={selectedType?.maxSizeInBytes}
                          uploadDetails={uploadDetails?.[fileName]}
                          onClear={() => setFileName("")}
                        />
                      </div>
                    )) ||
                    null}
                  {(uploadType === 4 && isEditable && (
                    <div className="col-12">
                      <Editor
                        label={intl.formatMessage({ id: "html_content" })}
                        value={""}
                        required={true}
                        input={{
                          name: "html_content",
                          onChange: setHtmlContent,
                          value: htmlContent,
                        }}
                        style={{ height: "320px" }}
                      />
                    </div>
                  )) ||
                    null}
                </div>
              </Section>
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  action: state.learningObjects?.action,
  certificates: state?.certificates?.data || [],
  enterprise: state?.global?.enterprise,
  error: state.learningObjects?.error,
  progress: state.uploads?.progress,
  record: state.learningObjects?.record,
  requesting: state.learningObjects?.requesting,
  rewards: state?.rewards?.data?.list || state?.rewards?.data || [],
  uploadDetails: state.uploads?.details,
  uploadErrors: state.uploads?.error,
  uploading: state.uploads?.uploading,
  user: state.self?.user,
});

const mapDispatchToProps = (dispatch) => ({
  resetUpload: () => dispatch(resetUpload()),
  uploadRequest: (data) => dispatch(uploadRequest(data)),
  createRequest: (data) => dispatch(createRequest(data)),
  detailsRequest: (id) => dispatch(detailsRequest(id)),
  updateDetailRequest: (data) => dispatch(updateDetailRequest(data)),
  certificateListRequest: (data) => dispatch(certificateListRequest(data)),
  rewardListRequest: (data) => dispatch(rewardListRequest(data)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(LearningObjectDetails);
